import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Magnet, ArrowRight, Search, Target, Share2, PenTool, MessageSquareHeart, LayoutTemplate, TrendingUp, Sparkles, Check, Download, Lock, BookOpen, Bot, FileText, CreditCard, Quote, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { api, API, formatApiErrorDetail } from "@/lib/api";

const PAYMENT_URL = "https://www.masterteacher33.com/donation";
const HERO_IMAGE = "https://customer-assets-cm19k8pv.emergentagent.net/job_acquire-ai-4/artifacts/xr5s9yfg_INSTANT%20ACCESS%20%24197.png";

const TESTIMONIALS = [
  {
    quote: "Within 48 hours of receiving and implementing my 7 Step Customer Acquisition Campaign Report, I was amazed at the detail of my campaign strategy. This would have taken my agency weeks to produce. The lead profiles were scary accurate.",
    name: "Jason M.",
    title: "E-commerce Business Owner",
  },
  {
    quote: "I replaced a $3,000/month marketing consultant with my MT33 7 Step Customer Acquisition Campaign Report. The nurture sequences alone generated 23 new clients in the first month. Absolutely game-changing.",
    name: "Sarah R.",
    title: "Real Estate Agent",
  },
];

const AREAS = [
  { icon: Search, title: "Market Research", desc: "We scan your market's size, momentum, and gaps — mapping trends, competitor weaknesses, and the single positioning angle you can own, so you enter every conversation from a position of strength." },
  { icon: Target, title: "Lead Qualification & Scoring", desc: "We build a razor-sharp profile of your ideal buyer and a scoring model that separates ready-to-buy prospects from time-wasters — so your effort and budget only go where the money is." },
  { icon: Share2, title: "Platform Recommendations", desc: "We identify the platforms where your buyers actually spend attention and split your budget by audience match, with the best posting times and content style for each channel." },
  { icon: PenTool, title: "Campaign Content", desc: "Ready-to-post hooks, headlines, body copy, hashtags and CTAs — plus a detailed content calendar engineered to stop the scroll and drive immediate action." },
  { icon: MessageSquareHeart, title: "Nurture Sequences", desc: "DM templates and a full email follow-up sequence that answers objections before they're spoken and keeps your offer top-of-mind until the prospect is ready to buy." },
  { icon: LayoutTemplate, title: "Landing Page Suggestions", desc: "Conversion-focused landing page blueprints — headline variations, page structure, form fields and trust elements — built to turn clicks into customers." },
  { icon: TrendingUp, title: "Conversion Metrics & ROI", desc: "A projected funnel, ROI estimate, KPI targets and prioritized A/B tests, so you know exactly what to expect and precisely what to optimize next." },
];

const Brand = () => (
  <div className="flex items-center gap-2">
    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald9 border border-gold/40"><Magnet className="h-5 w-5 text-gold" /></div>
    <div className="leading-tight"><span className="font-display text-lg font-bold">MT33</span><span className="ml-1 text-xs text-gold">Autonomous AI</span></div>
  </div>
);

const OfferDialog = ({ tier, title, price, description, icon: Icon, testid, highlight }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("form");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Enter your full name and email"); return; }
    setSaving(true);
    try {
      await api.post("/orders", { name, email, tier });
      setStep("pay");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    } finally { setSaving(false); }
  };

  const onOpenChange = (v) => { setOpen(v); if (!v) { setStep("form"); setName(""); setEmail(""); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid={testid} className={`w-full rounded-full py-6 font-bold text-white ${highlight ? "bg-gradient-to-r from-emerald9 via-royal to-gold" : "bg-gradient-to-r from-emerald9 to-gold"}`}>
          {title.includes("Agent") ? "Build My AI Agent" : "Get the Report"} — {price} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="border-border bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-gold"><Icon className="h-5 w-5" /> {title} — {price}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">{description}</DialogDescription>
        </DialogHeader>
        {step === "form" ? (
          <div className="space-y-3 pt-2">
            <Input data-testid={`${testid}-name`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="bg-muted/40 border-border" />
            <Input data-testid={`${testid}-email`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className="bg-muted/40 border-border" />
            <Button onClick={submit} disabled={saving} data-testid={`${testid}-continue`} className="w-full rounded-full bg-gradient-to-r from-emerald9 via-royal to-gold py-6 font-bold text-white">
              {saving ? "Please wait…" : <>Continue to Payment <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-2 text-center" data-testid={`${testid}-pay`}>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald9 border border-gold/40"><CreditCard className="h-6 w-6 text-gold" /></div>
            <p className="text-sm text-muted-foreground">Thanks, <span className="text-foreground font-medium">{name.split(" ")[0]}</span>! Click below to complete your <span className="text-gold">{price}</span> payment.</p>
            <a href={PAYMENT_URL} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-paylink`}>
              <Button className="w-full rounded-full bg-gradient-to-r from-emerald9 via-royal to-gold py-6 text-base font-bold text-white">Pay {price} Now <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </a>
            <p className="rounded-lg border border-gold/30 bg-emerald9/20 px-4 py-3 text-sm text-muted-foreground">Once payment is made, you will receive an email with further instructions.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Home() {
  const [lead, setLead] = useState({ name: "", email: "" });
  const [ebookDone, setEbookDone] = useState(false);
  const [sendingLead, setSendingLead] = useState(false);

  const getEbook = async () => {
    if (!lead.name.trim() || !lead.email.trim()) { toast.error("Enter your name and email"); return; }
    setSendingLead(true);
    try {
      await api.post("/leads", { name: lead.name, email: lead.email });
      const a = document.createElement("a");
      a.href = `${API}/ebook`;
      a.download = "Turn-Any-Business-Into-A-Customer-Magnet.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setEbookDone(true);
      toast.success("Your free ebook is downloading!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    } finally { setSendingLead(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3"><Brand /></div>
      </header>

      {/* Uploaded image */}
      <section className="mx-auto max-w-5xl px-5 pt-8">
        <motion.img initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          src={HERO_IMAGE} alt="MT33 Autonomous AI Customer Acquisition Agent" data-testid="hero-image"
          className="mx-auto w-full rounded-2xl border border-gold/25 object-contain shadow-[0_0_40px_rgba(212,175,55,0.12)]" />
      </section>

      {/* Hero text */}
      <section className="mx-auto max-w-3xl px-5 py-12 text-center">
        <div className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-emerald9/30 px-4 py-1.5">
          <Sparkles className="h-3.5 w-3.5 text-gold" />
          <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-gold">World's First Autonomous Customer Acquisition AI</span>
        </div>
        <h1 className="font-display text-4xl font-black leading-[1.05] sm:text-5xl">Turn Any Business Into A <span className="text-gold-gradient">Customer Magnet.</span></h1>
        <p className="mx-auto mt-5 max-w-xl text-base text-muted-foreground">No fluff. No BS. A fully autonomous AI that finds your buyers and builds the campaign that converts them — choose your level below.</p>
      </section>

      {/* Two offers */}
      <section className="mx-auto grid max-w-4xl gap-6 px-5 pb-4 md:grid-cols-2">
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex flex-col rounded-2xl border border-border bg-card p-7">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-emerald9 border border-gold/20"><FileText className="h-5 w-5 text-gold" /></div>
          <h3 className="font-display text-xl font-bold">7-Step Customer Acquisition Campaign Report</h3>
          <p className="mt-2 flex-1 text-sm text-muted-foreground">A complete, done-for-you 50–75 page campaign: market research, qualified leads, platform strategy, ready-to-post content, nurture sequences, landing pages and ROI projections — delivered as a detailed PDF report.</p>
          <div className="mt-5 flex items-end gap-2"><span className="font-display text-4xl font-black text-gold">$197</span></div>
          <div className="mt-4">
            <OfferDialog tier="report_197" testid="offer-report-btn" title="7-Step Report" price="$197" icon={FileText}
              description="Enter your details, then complete payment. Once payment is made, you'll receive an email with further instructions." />
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="relative flex flex-col rounded-2xl border-2 border-gold bg-card p-7 gold-glow">
          <div className="absolute right-0 top-0 rounded-bl-xl bg-gradient-to-r from-royal to-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">Full Power</div>
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-emerald9 border border-gold/20"><Bot className="h-5 w-5 text-gold" /></div>
          <h3 className="font-display text-xl font-bold">Complete Customized Autonomous AI Agent</h3>
          <p className="mt-2 flex-1 text-sm text-muted-foreground">A fully customized autonomous AI agent that runs your entire customer acquisition — research, lead generation, content, nurture and conversion — continuously, so your business becomes a self-running customer magnet.</p>
          <div className="mt-5 flex items-end gap-2"><span className="font-display text-4xl font-black text-gold">$4,997</span></div>
          <div className="mt-4">
            <OfferDialog tier="agent_4997" testid="offer-agent-btn" title="Autonomous AI Agent" price="$4,997" icon={Bot} highlight
              description="Enter your details, then complete payment. Once payment is made, you'll receive an email with further instructions." />
          </div>
        </motion.div>
      </section>

      {/* 7 clickable areas */}
      <section className="mx-auto max-w-5xl px-5 py-14">
        <div className="mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">What's Inside The Report</p>
          <h2 className="mt-2 font-display text-3xl font-bold">7 Areas. Click Any To See What It Covers.</h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {AREAS.map((a, i) => (
            <Dialog key={a.title}>
              <DialogTrigger asChild>
                <button data-testid={`area-${i}`} className="group flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-gold/50">
                  <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-emerald9 border border-gold/20"><a.icon className="h-5 w-5 text-gold" /></div>
                  <div className="min-w-0"><span className="font-display text-sm font-bold text-border">0{i + 1}</span><p className="text-sm font-medium leading-tight">{a.title}</p></div>
                  <ArrowRight className="ml-auto h-4 w-4 flex-none text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-gold" />
                </button>
              </DialogTrigger>
              <DialogContent className="border-border bg-card">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 font-display text-gold"><a.icon className="h-5 w-5" /> {a.title}</DialogTitle>
                  <DialogDescription className="pt-2 text-sm leading-relaxed text-muted-foreground">{a.desc}</DialogDescription>
                </DialogHeader>
              </DialogContent>
            </Dialog>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="mx-auto max-w-5xl px-5 pb-4">
        <div className="mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Real Results</p>
          <h2 className="mt-2 font-display text-3xl font-bold">What Our Clients Are Saying</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              data-testid={`testimonial-${i}`}
              className="relative flex flex-col rounded-2xl border border-gold/25 bg-card p-7"
            >
              <Quote className="mb-4 h-8 w-8 flex-none text-gold/70" />
              <div className="mb-4 flex gap-0.5" data-testid={`testimonial-${i}-stars`}>
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} className="h-4 w-4 fill-gold text-gold" />
                ))}
              </div>
              <p className="flex-1 text-sm leading-relaxed text-muted-foreground">"{t.quote}"</p>
              <div className="mt-5 border-t border-border pt-4">
                <p className="font-display text-sm font-bold text-foreground">{t.name}</p>
                <p className="text-xs text-gold">{t.title}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Free ebook */}
      <section className="mx-auto max-w-2xl px-5 pb-20">
        <div className="overflow-hidden rounded-2xl border border-gold/40 bg-card gold-glow">
          <div className="flex items-center gap-3 border-b border-border bg-emerald9/40 px-6 py-4">
            <BookOpen className="h-6 w-6 text-gold" />
            <div><p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">Free Ebook</p><h3 className="font-display text-lg font-bold">Turn Any Business Into A Customer Magnet</h3></div>
          </div>
          <div className="p-6">
            {ebookDone ? (
              <div className="py-6 text-center" data-testid="ebook-success">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald9 border border-gold/40"><Check className="h-6 w-6 text-gold" /></div>
                <p className="font-medium">Your ebook is downloading!</p>
                <p className="text-sm text-muted-foreground">If it didn't start, <a href={`${API}/ebook`} className="text-gold underline" data-testid="ebook-manual-link">click here to download</a>.</p>
              </div>
            ) : (
              <>
                <p className="mb-4 text-sm text-muted-foreground">Enter your name and email and we'll instantly deliver the free ebook — the no-fluff field guide to autonomous customer acquisition.</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input data-testid="ebook-name-input" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name" className="bg-muted/40 border-border" />
                  <Input data-testid="ebook-email-input" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="you@email.com" className="bg-muted/40 border-border" />
                </div>
                <Button onClick={getEbook} disabled={sendingLead} data-testid="ebook-submit-btn" className="mt-4 w-full rounded-full bg-gradient-to-r from-emerald9 via-royal to-gold py-6 font-bold text-white">
                  {sendingLead ? "Preparing…" : <><Download className="mr-2 h-4 w-4" /> Send Me The Free Ebook</>}
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 sm:flex-row">
          <p className="text-xs text-muted-foreground">Generated by MT33 Autonomous AI · © {new Date().getFullYear()}</p>
          <Link to="/admin" data-testid="admin-link" className="flex items-center gap-1 text-xs text-muted-foreground/60 transition-colors hover:text-gold"><Lock className="h-3 w-3" /> Admin</Link>
        </div>
      </footer>
    </div>
  );
}
